import React, { useState } from 'react'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined'
import MessageView from './messageView'
import { postToApi } from 'actions'
import { useStateValue } from 'context/store'
import { handleErrorMessage } from 'utils/customHooks/useApiRequest'

const useChangePassFormStyles = makeStyles(() => ({
   changePasswordContainer: {
      height: '100%',
      width: 320
   }
}))

function EditProfileImage({
   handleClose,
   setParentView,
   userName,
   userEntity
}) {
   const classes = useChangePassFormStyles()
   const [currentView, setCurrentView] = useState('formView')

   return (
      <Grid className={classes.changePasswordContainer}>
         {currentView === 'formView' && (
            <FormView
               setParentView={setParentView}
               setCurrentView={setCurrentView}
               userName={userName}
               userEntity={userEntity}
            />
         )}
         {currentView === 'successMessageView' && (
            <MessageView
               title={'Imagen de Perfil'}
               handleClose={handleClose}
               successMessage={'Tu imagen de perfil fue cargada exitosamente'}
            />
         )}
         {currentView === 'errorMessageView' && (
            <MessageView
               title={'Imagen de Perfil'}
               error={true}
               setCurrentView={setCurrentView}
            />
         )}
      </Grid>
   )
}

export default EditProfileImage

// -------------------------------

const useFormViewStyles = makeStyles(theme => ({
   formViewContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: theme.spacing(2)
   },
   titleContainer: {
      display: 'flex',
      marginBottom: theme.spacing(2)
   },
   title: {
      fontSize: 16,
      fontWeight: 'bold',
      margin: 'auto'
   },
   button: {
      width: '100%',
      color: 'white',
      backgroundColor: '#3264DC',
      '&:hover': {
         color: 'white',
         backgroundColor: '#3264DC'
      }
   },
   clickable: {
      cursor: 'pointer'
   },
   userName: {
      fontSize: 18,
      fontWeight: 'bold',
      textTransform: 'capitalize',
      marginTop: theme.spacing(2)
   },
   userEntity: {
      fontSize: 14,
      color: '#8e8e8e',
      marginBottom: theme.spacing(2)
   },
   loadImageButtonContainer: {
      width: 140,
      height: 140,
      boxShadow: '0px 1px 10px #00000029',
      border: '4px solid #FFFFFF',
      borderRadius: '50%'
   },
   loadImageButton: {
      backgroundColor: '#8A8FAB',
      color: '#FFF'
   },
   imageContainer: {
      position: 'relative'
   },
   hoverLayer: {
      position: 'absolute',
      background: 'rgba(0, 0, 0, 0.5)',
      color: '#FFF'
   },
   centerElements: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
   },
   circleElement: {
      width: 133,
      height: 133,
      borderRadius: '50%'
   }
}))

function getBase64(file) {
   return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
   })
}

function FormView({ setParentView, setCurrentView, userName, userEntity }) {
   const classes = useFormViewStyles()
   const [{ userAvatar = {} }, dispatch] = useStateValue()

   const [imageSrc, setImageSrc] = useState(userAvatar.photo || null)
   const [uploadedImage, setUploadedImage] = useState(null)
   const [showOnHover, setShowOnHover] = useState({ display: 'none' })

   function onImageChange(event) {
      const image = event.target.files[0]
      setUploadedImage(image)
      const urlObject = URL.createObjectURL(image)
      setImageSrc(urlObject)
   }

   async function onUploadImage() {
      try {
         // NOTA: No aplica useApiRequest por callback en el catch
         dispatch({ type: 'OPEN_SPINNER' })
         const multipartFile = new FormData()
         multipartFile.append('filePicture', uploadedImage)
         const url = `${process.env.REACT_APP_AUTHORIZATION_API}/user/profile/picture`
         await postToApi(url, multipartFile)
         getBase64(uploadedImage).then(photo =>
            dispatch({
               type: 'SET_USER_AVATAR_PHOTO',
               payload: { photo }
            })
         )
         setCurrentView('successMessageView')
      } catch (error) {
         setCurrentView('errorMessageView')
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `${handleErrorMessage(error)} (Imagen Perfil: Cargar)`,
               severity: 'error'
            }
         })
      } finally {
         dispatch({ type: 'CLOSE_SPINNER' })
      }
   }

   function handleBackButtonClick() {
      setParentView('profileView')
   }

   function displayOnHover() {
      setShowOnHover({ display: 'flex' })
   }

   function hideOnBlur() {
      setShowOnHover({ display: 'none' })
   }

   return (
      <Grid className={classes.formViewContainer}>
         <div className={classes.titleContainer}>
            <ArrowBackIcon
               className={classes.clickable}
               onClick={handleBackButtonClick}
            />
            <Typography className={classes.title}>Imagen de Perfil</Typography>
         </div>
         <Grid className={classes.centerElements}>
            <div
               className={`${classes.loadImageButtonContainer} ${classes.centerElements}`}
            >
               <input
                  id="loadProfileImage"
                  type="file"
                  accept="image/jpg,image/jpeg,image/png"
                  onChange={onImageChange}
                  hidden
               />
               <label
                  htmlFor="loadProfileImage"
                  className={`${classes.clickable} ${classes.centerElements}`}
               >
                  {imageSrc ? (
                     <div
                        className={`${classes.circleElement} ${classes.centerElements} ${classes.imageContainer}`}
                        onMouseEnter={displayOnHover}
                        onMouseLeave={hideOnBlur}
                     >
                        <img
                           id="profileImage"
                           src={imageSrc}
                           className={classes.circleElement}
                        />
                        <div
                           className={`${classes.circleElement} ${classes.centerElements} ${classes.hoverLayer}`}
                           style={showOnHover}
                        >
                           <SelectYourPicture />
                        </div>
                     </div>
                  ) : (
                     <div
                        className={`${classes.circleElement} ${classes.centerElements} ${classes.loadImageButton}`}
                     >
                        <SelectYourPicture />
                     </div>
                  )}
               </label>
            </div>
            <Grid className={classes.userName}>{userName}</Grid>
            <Grid className={classes.userEntity}>{userEntity}</Grid>
         </Grid>
         <Button
            className={classes.button}
            type="submit"
            variant="contained"
            onClick={onUploadImage}
            disabled={!uploadedImage}
         >
            Cargar Imagen
         </Button>
      </Grid>
   )
}

// ------------------------------

const selectYourPictureStyles = makeStyles(theme => ({
   cameraIcon: {
      width: 32,
      height: 28,
      marginBottom: theme.spacing(1)
   }
}))

function SelectYourPicture() {
   const classes = selectYourPictureStyles()
   return (
      <>
         <CameraAltOutlinedIcon className={classes.cameraIcon} />
         <div>SELECCIONA</div>
         <div>TU FOTO</div>
      </>
   )
}
